import PendingCatalogRequests, { CatalogPublicationFilter, usePendingCatalogRequests } from "../catalogApprovals/PendingCatalogRequests";
import useCatalogAccess from "../useCatalogAccess";
import CatalogCreationPendingNotice, { isPendingCatalogCreation } from "../CatalogCreationPendingNotice";
import { catalogRequestError, importInternalCodeError } from "../internalCatalogCodes";
import { useCatalogTaxonomyText } from "../catalogTaxonomyMessages";
import CorporateRecordBadge from "../CorporateRecordBadge";
import InventoryPolicyChip from "../InventoryPolicyChip";
import { managedByAdmin } from "../../utils/tenderManagement";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import AddSubcategoryPopup from "../popups/AddSubcategoryPopup";
import EditTaxonomyPopup from "../popups/EditTaxonomyPopup";
import { TaxonomyMergeActions } from "../popups/TaxonomyMergePopup";
import ItemsFamiliesImportPopup from "../ItemsFamiliesImportPopup";
import TaxonomyItemsPopup, {
  makeTaxonomyItemsScope,
  TaxonomyItemsLink,
} from "../popups/TaxonomyItemsPopup";
import {

  buildSearchBlob,
  formatItemsFamiliesDate,
  inventoryPolicyHint,
  inventoryPolicyLabel,
  normalizeImportPreviewRows,
  readItemsFamiliesUser,
  resolveTaxonomyInventoryPolicy,
  truncateItemsFamiliesText,
} from "../itemsFamiliesUi";

function buildSubcategorySearchBlob(subcategory) {
  return buildSearchBlob([
    subcategory?.code,
    subcategory?.name,
    subcategory?.category?.name,
    subcategory?.category?.subfamily?.name,
    subcategory?.category?.subfamily?.family?.name,
    subcategory?.description,
  ]);
}

export default function SubcategoriesTab({ businessUnitId }) {
  const t = useCatalogTaxonomyText();
  const currentUser = readItemsFamiliesUser();
  const activeBusinessUnitId = businessUnitId || currentUser?.active_business_unit_id;
  const access = useCatalogAccess(activeBusinessUnitId);
  const viewPermission = "purchasing.categories.view";
  const editPermissions = ["purchasing.categories.create", "purchasing.categories.update"];
  const exportPermissions = [viewPermission, "purchasing.categories.export"];
  const canViewSubcategories = access.can(viewPermission);
  const canCreateSubcategory = access.can("purchasing.categories.create");
  const canEditSubcategory = access.can(editPermissions);
  const canExport = access.can(exportPermissions);
  const importPermissions = ["purchasing.categories.create", "purchasing.categories.import"];
  const canImport = access.can(importPermissions);
  const isMobile = useIsMobile();
  const fileInputRef = useRef(null);

  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [notice, setNotice] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isAddPopupOpen, setIsAddPopupOpen] = useState(false);
  const [publicationFilter, setPublicationFilter] = useState("published");
  const pendingCatalog = usePendingCatalogRequests({ entityType: "subcategory", businessUnitId: activeBusinessUnitId,
    enabled: canViewSubcategories && publicationFilter === "pending", refreshKey: isAddPopupOpen });
  const [editing, setEditing] = useState(null);
  const [itemsScope, setItemsScope] = useState(null);
  const importContext = useRef(null);
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [isImportPopupOpen, setIsImportPopupOpen] = useState(false);
  const [importError, setImportError] = useState("");
  const [importing, setImporting] = useState(false);

  const fetchSubcategories = useCallback(
    async ({ quiet = false } = {}) => {
      if (!canViewSubcategories) {
        setSubcategories([]);
        setLoading(false);
        return;
      }

      if (!quiet) {
        setLoading(true);
      }

      const stamp = access.capture();
      try {
        const options = access.requestConfig(viewPermission);
        const response = await axios.get("/api/purchasing/subcategories", { ...options, params: { ...options.params, catalog: 1 } });
        if (!access.isCurrent(stamp, viewPermission)) return;
        const sorted = [...(response.data || [])].sort((left, right) => {
          const leftCategory = String(left?.category?.name || "");
          const rightCategory = String(right?.category?.name || "");
          if (leftCategory !== rightCategory) {
            return leftCategory.localeCompare(rightCategory, undefined, { sensitivity: "base" });
          }
          return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
            sensitivity: "base",
          });
        });
        setSubcategories(sorted);
      } catch (requestError) {
        if (!access.isCurrent(stamp, viewPermission)) return;
        console.error("[SUBCATEGORIES][LOAD][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not load the subcategory list for this business unit.",
        });
      } finally {
        if (!quiet && access.isCurrent(stamp, viewPermission)) {
          setLoading(false);
        }
      }
    },
    [canViewSubcategories, access, viewPermission]
  );

  useEffect(() => {
    fetchSubcategories();
  }, [fetchSubcategories]);

  const filteredSubcategories = useMemo(() => {
    const query = search.trim().toLowerCase();
    return subcategories.filter(
      (subcategory) => !query || buildSubcategorySearchBlob(subcategory).includes(query)
    );
  }, [search, subcategories]);

  const stats = useMemo(() => {
    const visibleCategories = new Set(
      filteredSubcategories.map((subcategory) => subcategory?.category?.name).filter(Boolean)
    ).size;
    const visibleFamilies = new Set(
      filteredSubcategories.map((subcategory) => subcategory?.category?.subfamily?.family?.name).filter(Boolean)
    ).size;
    const described = filteredSubcategories.filter((subcategory) =>
      String(subcategory?.description || "").trim()
    ).length;

    return {
      visible: filteredSubcategories.length,
      categories: visibleCategories,
      families: visibleFamilies,
      described,
    };
  }, [filteredSubcategories]);

  const exportToExcel = async () => {
    if (!access.can(exportPermissions) || publicationFilter === "pending") return;
    const stamp = access.capture();
    try {
      const options = access.requestConfig(exportPermissions);
      const response = await axios.get("/api/purchasing/subcategories", { ...options, params: { ...options.params, catalog: 1, export: 1 } });
      if (!access.isCurrent(stamp, exportPermissions)) return;
      const query = search.trim().toLowerCase();
      const exportRecords = (response.data || []).filter((row) => !query || buildSubcategorySearchBlob(row).includes(query));
    if (!exportRecords.length) {
      setNotice({
        tone: "info",
        message: "There are no subcategory rows in the current slice to export.",
      });
      return;
    }

    const exportRows = exportRecords.map((subcategory) => ({
      Code: subcategory?.code || "",
      Name: subcategory?.name || "",
      Category: subcategory?.category?.name || "",
      Subfamily: subcategory?.category?.subfamily?.name || "",
      Family: subcategory?.category?.subfamily?.family?.name || "",
      InventoryPolicy: inventoryPolicyLabel(resolveTaxonomyInventoryPolicy(subcategory, "subcategory").policy),
      InventoryPolicySource: inventoryPolicyHint(resolveTaxonomyInventoryPolicy(subcategory, "subcategory"), "subcategory"),
      Description: subcategory?.description || "",
      CreatedAt: subcategory?.created_at || "",
      UpdatedAt: subcategory?.updated_at || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Subcategories");
    XLSX.writeFile(workbook, "subcategories.xlsx");

    setNotice({
      tone: "success",
      message: `${exportRecords.length} subcategory rows exported to Excel.`,
    });
    } catch (_error) {
      if (access.isCurrent(stamp, exportPermissions)) setNotice({ tone: "error", message: "Could not export this catalog. Please try again." });
    }
  };

  const updateInventoryPolicy = async (subcategory, inventoryPolicy) => {
    if (!access.can(editPermissions) || managedByAdmin(subcategory)) return;
    const stamp = access.capture();
    try {
      const response = await axios.put(`/api/purchasing/subcategories/${subcategory.id}`, {
        inventory_policy: inventoryPolicy || null,
      }, access.requestConfig(editPermissions));
      if (!access.isCurrent(stamp, editPermissions)) return;
      if (isPendingCatalogCreation(response.data)) {
        setPendingRequest(response.data.request);
        return;
      }
      setSubcategories((current) =>
        current.map((entry) => (Number(entry.id) === Number(subcategory.id) ? { ...entry, ...response.data } : entry))
      );
      setNotice({ tone: "success", message: `${subcategory?.name || "Subcategory"} inventory policy updated.` });
    } catch (requestError) {
      if (!access.isCurrent(stamp, editPermissions)) return;
      console.error("[SUBCATEGORIES][POLICY][ERROR]", requestError);
      setNotice({ tone: "error", message: "Could not update this inventory policy." });
      return false;
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    const input = event.target;
    if (!file || !access.can(importPermissions)) return;

    const stamp = access.capture();
    const reader = new FileReader();

    reader.onload = (loadEvent) => {
      if (!access.isCurrent(stamp, importPermissions)) return;
      try {
        const data = new Uint8Array(loadEvent.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const rawRows = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
          blankrows: false,
        });
        const normalizedRows = normalizeImportPreviewRows(rawRows);

        if (!normalizedRows.length) {
          setNotice({
            tone: "error",
            message: "The selected file did not contain recognizable subcategory rows.",
          });
          input.value = "";
          return;
        }

        importContext.current = stamp;
        setImportRows(normalizedRows);
        setImportFileName(file.name);
        setImportError("");
        setIsImportPopupOpen(true);
      } catch (requestError) {
        console.error("[SUBCATEGORIES][IMPORT][PARSE][ERROR]", requestError);
        setNotice({
          tone: "error",
          message: "We could not read the selected file. Please verify the format and try again.",
        });
      } finally {
        input.value = "";
      }
    };

    reader.onerror = () => {
      if (!access.isCurrent(stamp, importPermissions)) return;
      setNotice({
        tone: "error",
        message: "We could not read the selected file. Please try again.",
      });
      input.value = "";
    };

    reader.readAsArrayBuffer(file);
  };

  const handleConfirmImport = async () => {
    if (!importRows.length || importing || !importContext.current || !access.isCurrent(importContext.current, importPermissions)) return;

    const codeError = importInternalCodeError(importRows, subcategories);
    if (codeError) { setImportError(codeError); return; }

    const stamp = access.capture();
    setImporting(true);
    setImportError("");

    try {
      const response = await axios.post("/api/purchasing/subcategories/import", {
        business_unit_id: activeBusinessUnitId,
        data: importRows,
      }, access.requestConfig(importPermissions));
      if (!access.isCurrent(stamp, importPermissions)) return;

      setIsImportPopupOpen(false);
      setImportRows([]);
      setImportFileName("");
      await fetchSubcategories({ quiet: true });
      if (!access.isCurrent(stamp, importPermissions)) return;
      setNotice({
        tone: "success",
        message: `${response.data.inserted || 0} subcategories imported successfully.`,
      });
    } catch (requestError) {
      if (!access.isCurrent(stamp, importPermissions)) return;
      console.error("[SUBCATEGORIES][IMPORT][ERROR]", requestError);
      const details = requestError?.response?.data?.details;
      setImportError(
        Array.isArray(details) && details.length
          ? details.join(" ")
          : catalogRequestError(requestError, "We could not import this subcategory file.")
      );
    } finally {
      if (access.isCurrent(stamp, importPermissions)) setImporting(false);
    }
  };

  const handleAdded = async (newSubcategory) => {
    await fetchSubcategories({ quiet: true });
    setNotice({
      tone: "success",
      message: `${newSubcategory?.name || "Subcategory"} created successfully.`,
    });
  };

  const handleEdited = (updated) => {
    setSubcategories((current) => current.map((entry) => Number(entry.id) === Number(updated.id) ? { ...entry, ...updated } : entry));
    setNotice({ tone: "success", message: `${updated.name} updated successfully.` });
  };

  const renderEditButton = (subcategory) => canEditSubcategory && !managedByAdmin(subcategory) ? (
    <button type="button" className="spx-btn spx-btn--ghost" aria-label={t(`Edit Subcategory: ${subcategory.name}`)} onClick={() => setEditing(subcategory)}>{t("Edit")}</button>
  ) : null;

  if (!canViewSubcategories) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("You do not have permission to access subcategories.")}</div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="spx-card pu-taxonomy">

        <div className="spx-empty">{t("Loading subcategories...")}</div>
      </section>
    );
  }

  return (
    <>
      <section className="spx-card pu-taxonomy">


        <div className="spx-head">
          <div className="spx-headMain">
            <div className="spx-kicker">{t("Family → Subfamily → Category → Subcategory → Item")}</div>
            <h2 className="spx-title">{t("Subcategories")}</h2>
            <p className="spx-subtitle">{t("Subcategories refine a category before the individual item.")}</p>
          </div>

          <div className="spx-actions">
            <TaxonomyMergeActions kind="subcategory" records={subcategories} businessUnitId={activeBusinessUnitId} onChanged={() => fetchSubcategories({ quiet: true })} />
            {canCreateSubcategory ? (
              <button type="button" className="spx-btn spx-btn--primary" onClick={() => setIsAddPopupOpen(true)}>{t("+ Add Subcategory")} </button>
            ) : null}
            {canImport && <button type="button" className="spx-btn spx-btn--success" onClick={() => fileInputRef.current?.click()}>{t("Import Excel/CSV")} </button>}
            {canExport && <button type="button" className="spx-btn spx-btn--warning" disabled={publicationFilter === "pending"} onClick={exportToExcel}>{t("Export Excel")} </button>}
            {canImport && <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: "none" }}
              onChange={handleImportFile}
            />}
          </div>
        </div>


        <div className="spx-toolbar">
          <div className="ifm-toolbarGrid ifm-toolbarGrid--two">
            <input
              type="search"
              className="spx-search"
              aria-label={t("Search")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by code, subcategory, category, subfamily, family or description...")}
            />
            {publicationFilter !== "pending" && <div className="spx-note">{t("Showing")} <strong>{filteredSubcategories.length}</strong> {t("of")} <strong>{subcategories.length}</strong> {t("subcategories.")} </div>}
          </div>
        </div>

        <CatalogPublicationFilter value={publicationFilter} onChange={setPublicationFilter} />

        {notice ? <div className={`spx-banner spx-banner--${notice.tone}`}>{t(notice.message)}</div> : null}

        {publicationFilter === "pending" ? <PendingCatalogRequests entityType="subcategory" businessUnitId={activeBusinessUnitId} state={pendingCatalog} search={search} /> : <>
        <div className="spx-stats pu-taxonomy-stats">
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Visible Subcategories")}</div>
            <div className="spx-statValue">{stats.visible}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Categories Represented")}</div>
            <div className="spx-statValue">{stats.categories}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("Families Represented")}</div>
            <div className="spx-statValue">{stats.families}</div>
          </div>
          <div className="spx-stat">
            <div className="spx-statLabel">{t("With Description")}</div>
            <div className="spx-statValue">{stats.described}</div>
          </div>
        </div>

        {!filteredSubcategories.length ? (
          <div className="spx-empty">{t("No subcategories match the current search.")}</div>
        ) : isMobile ? (
          <div className="spx-mobileList">
            {filteredSubcategories.map((subcategory) => (
              <article key={subcategory.id} className="spx-mobileCard">
                {(() => {
                  const policy = resolveTaxonomyInventoryPolicy(subcategory, "subcategory");
                  return (
                    <>
                <div className="spx-mobileTop">
                  <div>
                    <div className="spx-code">{subcategory?.code || `SUBCATEGORY-${subcategory.id}`}</div>
                    <TaxonomyItemsLink
                      scope={makeTaxonomyItemsScope("subcategory", subcategory?.id, subcategory?.name)}
                      onOpen={setItemsScope}
                      strong
                      className="spx-strong"
                    >
                      {truncateItemsFamiliesText(subcategory?.name || "-", 48)}<CorporateRecordBadge record={subcategory} />
                    </TaxonomyItemsLink>
                  </div>
                  <InventoryPolicyChip policy={policy.policy} name={subcategory.name} editable={canEditSubcategory && !managedByAdmin(subcategory)} onChange={(value) => updateInventoryPolicy(subcategory, value)} />
                </div>
                    </>
                  );
                })()}

                <div className="ifm-tagRow">
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope("category", subcategory?.category?.id, subcategory?.category?.name)}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {subcategory?.category?.name || t("No category")}
                  </TaxonomyItemsLink>
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope(
                      "subfamily",
                      subcategory?.category?.subfamily?.id,
                      subcategory?.category?.subfamily?.name
                    )}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {subcategory?.category?.subfamily?.name || t("No subfamily")}
                  </TaxonomyItemsLink>
                  <TaxonomyItemsLink
                    scope={makeTaxonomyItemsScope(
                      "family",
                      subcategory?.category?.subfamily?.family?.id,
                      subcategory?.category?.subfamily?.family?.name
                    )}
                    onOpen={setItemsScope}
                    className="ifm-tag"
                  >
                    {subcategory?.category?.subfamily?.family?.name || t("No family")}
                  </TaxonomyItemsLink>
                </div>

                <div className="spx-mobileRows">
                  <div>
                    <div className="spx-mobileLabel">{t("Created")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(subcategory?.created_at)}</div>
                  </div>
                  <div>
                    <div className="spx-mobileLabel">{t("Updated")}</div>
                    <div className="spx-mobileValue">{formatItemsFamiliesDate(subcategory?.updated_at)}</div>
                  </div>
                </div>

                <div className="spx-mobileValue" style={{ marginTop: 10 }}>
                  {subcategory?.description || "-"}
                </div>
                {canEditSubcategory && !managedByAdmin(subcategory) && <div className="spx-actions" style={{ marginTop: 10 }}>{renderEditButton(subcategory)}</div>}
              </article>
            ))}
          </div>
        ) : (
          <div className="spx-tableWrap">
            <table className="spx-table ifm-table--medium">
              <thead>
                <tr>
                  <th>{t("Code")}</th>
                  <th>{t("Subcategory")}</th>
                  <th>{t("Category")}</th>
                  <th>{t("Subfamily")}</th>
                  <th>{t("Family")}</th>
                  <th>{t("Inventory Policy")}</th>
                  <th>{t("Description")}</th>
                  <th>{t("Created")}</th>
                  <th>{t("Updated")}</th>
                  {canEditSubcategory && <th>{t("Actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSubcategories.map((subcategory) => (
                  (() => {
                    const policy = resolveTaxonomyInventoryPolicy(subcategory, "subcategory");
                    return (
                      <tr key={subcategory.id}>
                        <td title={subcategory?.code || `SUBCATEGORY-${subcategory.id}`}>
                          <span className="spx-code">{subcategory?.code || `SUBCATEGORY-${subcategory.id}`}</span>
                        </td>
                        <td title={subcategory?.name || "-"}>
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("subcategory", subcategory?.id, subcategory?.name)}
                            onOpen={setItemsScope}
                            strong
                            className="spx-strong"
                          >
                            {truncateItemsFamiliesText(subcategory?.name || "-", 48)}<CorporateRecordBadge record={subcategory} />
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope("category", subcategory?.category?.id, subcategory?.category?.name)}
                            onOpen={setItemsScope}
                          >
                            {subcategory?.category?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope(
                              "subfamily",
                              subcategory?.category?.subfamily?.id,
                              subcategory?.category?.subfamily?.name
                            )}
                            onOpen={setItemsScope}
                          >
                            {subcategory?.category?.subfamily?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td className="spx-muted">
                          <TaxonomyItemsLink
                            scope={makeTaxonomyItemsScope(
                              "family",
                              subcategory?.category?.subfamily?.family?.id,
                              subcategory?.category?.subfamily?.family?.name
                            )}
                            onOpen={setItemsScope}
                          >
                            {subcategory?.category?.subfamily?.family?.name || "-"}
                          </TaxonomyItemsLink>
                        </td>
                        <td>
                          <InventoryPolicyChip policy={policy.policy} name={subcategory.name} editable={canEditSubcategory && !managedByAdmin(subcategory)} onChange={(value) => updateInventoryPolicy(subcategory, value)} />
                        </td>
                        <td className="spx-muted" title={subcategory?.description || "-"}>
                          {truncateItemsFamiliesText(subcategory?.description || "-", 68)}
                        </td>
                        <td className="spx-muted">{formatItemsFamiliesDate(subcategory?.created_at)}</td>
                        <td className="spx-muted">{formatItemsFamiliesDate(subcategory?.updated_at)}</td>
                        {canEditSubcategory && <td>{renderEditButton(subcategory)}</td>}
                      </tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>}
      </section>

      {pendingRequest && <CatalogCreationPendingNotice request={pendingRequest} onClose={() => setPendingRequest(null)} />}

      <AddSubcategoryPopup
        existingRecords={subcategories}
        isOpen={isAddPopupOpen}
        onClose={() => setIsAddPopupOpen(false)}
        onAdded={handleAdded}
      />

      {editing && <EditTaxonomyPopup key={editing.id} kind="subcategory" record={editing} existingRecords={subcategories} onClose={() => setEditing(null)} onSaved={handleEdited} />}

      <TaxonomyItemsPopup scope={itemsScope} onClose={() => setItemsScope(null)} />

      <ItemsFamiliesImportPopup
        isOpen={canImport && isImportPopupOpen}
        onClose={() => {
          if (importing) return;
          setIsImportPopupOpen(false);
          setImportRows([]);
          setImportFileName("");
          setImportError("");
        }}
        title={t("Import Subcategories")}
        subtitle={t("Preview the incoming subcategory rows before they expand the catalog hierarchy.")}
        fileName={importFileName}
        rows={importRows}
        error={importError}
        isSubmitting={importing}
        confirmLabel={t("Confirm Subcategory Import")}
        onConfirm={handleConfirmImport}
      />
    </>
  );
}
